import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Flame, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * One-click streak resurrection landing.
 *
 * Comes from the daily 10am resurrection email. Token-bound, single-use,
 * 24h expiry. Requires the user to be logged in (router-protected
 * separately would be redundant — we just gate on `user`).
 */
export default function StreakRestorePage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [status, setStatus] = useState('idle'); // idle | working | done | error
  const [priorStreak, setPriorStreak] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!token || !user || loading || status !== 'idle') return;
    let cancelled = false;
    (async () => {
      setStatus('working');
      try {
        const jwt = localStorage.getItem('token');
        const res = await axios.post(
          `${API}/streak/restore?token=${encodeURIComponent(token)}`,
          {},
          { headers: jwt ? { Authorization: `Bearer ${jwt}` } : {} }
        );
        if (cancelled) return;
        setPriorStreak(res.data?.prior_streak ?? 0);
        setStatus('done');
      } catch (e) {
        if (cancelled) return;
        setErrorMsg(e.response?.data?.detail || 'Could not restore your streak.');
        setStatus('error');
      }
    })();
    return () => { cancelled = true; };
  }, [token, user, loading, status]);

  if (loading) return null;
  if (!user) return <Navigate to={`/login?redirect=/streak/restore?token=${token}`} />;
  if (!token) return <Navigate to="/app" replace />;

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6"
      style={{ backgroundColor: 'var(--isp-page)' }}
      data-testid="streak-restore-page"
    >
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="max-w-md w-full rounded-2xl shadow-sm border p-8 text-center"
        style={{
          background: 'var(--isp-glass)',
          borderColor: 'var(--isp-glass-border)',
        }}
      >
        <div className="mx-auto w-14 h-14 rounded-full bg-orange-50 flex items-center justify-center mb-5">
          <Flame className="w-7 h-7 text-orange-500" />
        </div>

        {status === 'working' && (
          <>
            <h1 className="text-2xl font-semibold tracking-tight" style={{ color: 'var(--isp-heading)' }}>Restoring your streak…</h1>
            <p className="text-sm mt-2" style={{ color: 'var(--isp-body, #71717a)' }}>One moment, this only takes a second.</p>
            <Loader2 className="w-5 h-5 mx-auto mt-6 animate-spin text-zinc-400" />
          </>
        )}

        {status === 'done' && (
          <>
            <h1 className="text-2xl font-semibold tracking-tight" data-testid="restore-success-title" style={{ color: 'var(--isp-heading)' }}>
              Welcome back.
            </h1>
            <p className="text-sm mt-2" style={{ color: 'var(--isp-body, #52525b)' }}>
              We restored your <span className="font-semibold text-orange-500">{priorStreak}-day streak</span>.
              Today already counts — keep the chain going.
            </p>
            <button
              onClick={() => navigate('/focus')}
              className="mt-6 w-full bg-[#002FA7] text-white py-3 rounded-xl font-medium hover:opacity-90 transition"
              data-testid="restore-cta-focus"
            >
              Start a focus session →
            </button>
            <button
              onClick={() => navigate('/app')}
              className="mt-3 text-sm text-zinc-500 hover:text-zinc-800"
              data-testid="restore-cta-dashboard"
            >
              Go to dashboard
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="mx-auto w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mb-4">
              <AlertTriangle className="w-6 h-6 text-amber-500" />
            </div>
            <h1 className="text-xl font-semibold tracking-tight">Couldn&apos;t restore</h1>
            <p className="text-sm text-zinc-500 mt-2" data-testid="restore-error-msg">{errorMsg}</p>
            <button
              onClick={() => navigate('/app')}
              className="mt-6 w-full bg-zinc-900 text-white py-3 rounded-xl font-medium hover:opacity-90"
            >
              Back to dashboard
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
}
